import type { Instrument } from "@skyware-group/iris-api-model";

import { BaseClient } from "../../clients/base/BaseClient";
import type { Paged } from "../_shared/pagination.types";
import { INSTRUMENTS_ENDPOINTS } from "./instruments.consts";
import type { InstrumentCreateRequest, InstrumentUpdateRequest, InstrumentsQueryParams } from "./instruments.types";

/**
 * Instruments API Client
 * Handles all instrument CRUD operations.
 * GET is available to all authenticated users (filtered by rights).
 * POST / PUT / DELETE are admin-only.
 */
export class InstrumentsClient extends BaseClient {
  /**
   * Fetch a page of instruments (filtered by rights for non-admins).
   * @param params - Optional pagination parameters.
   * @param signal - Optional abort signal.
   */
  async fetchInstruments(params?: InstrumentsQueryParams, signal?: AbortSignal | null): Promise<Paged<Instrument>> {
    return this.get<Paged<Instrument>>(INSTRUMENTS_ENDPOINTS.INSTRUMENTS, {
      params: params as Record<string, unknown>,
      signal,
    });
  }

  /**
   * Fetch a single instrument by ID. Response includes location and channels[].
   * @param id - Instrument ID.
   * @param signal - Optional abort signal.
   */
  async fetchInstrumentById(id: number, signal?: AbortSignal | null): Promise<Instrument> {
    return this.get<Instrument>(INSTRUMENTS_ENDPOINTS.INSTRUMENT_BY_ID(id), { signal });
  }

  /**
   * Create a new instrument. Admin only.
   * @param data - Instrument create request.
   * @param signal - Optional abort signal.
   */
  async createInstrument(data: InstrumentCreateRequest, signal?: AbortSignal | null): Promise<Instrument> {
    return this.post<Instrument>(INSTRUMENTS_ENDPOINTS.CREATE_INSTRUMENT, { body: data, signal });
  }

  /**
   * Update an existing instrument. Admin only.
   * @param id - Instrument ID.
   * @param data - Instrument update request.
   * @param signal - Optional abort signal.
   */
  async updateInstrument(id: number, data: InstrumentUpdateRequest, signal?: AbortSignal | null): Promise<Instrument> {
    return this.put<Instrument>(INSTRUMENTS_ENDPOINTS.UPDATE_INSTRUMENT(id), { body: data, signal });
  }

  /**
   * Delete an instrument. Admin only.
   * @param id - Instrument ID.
   * @param signal - Optional abort signal.
   */
  async deleteInstrument(id: number, signal?: AbortSignal | null): Promise<void> {
    return this.delete<never>(INSTRUMENTS_ENDPOINTS.DELETE_INSTRUMENT(id), { signal });
  }
}
